import Modal, { ModalFooter } from '@amzn/meridian/modal';
import React, { useEffect, useState } from 'react';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';

import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import _ from 'lodash';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';

const TenantLookupModal = ({ open, setOpen, selectedEntry, onConfirm }) => {
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [attributes, setAttributes] = useState({});
  const [newAttributeKey, setNewAttributeKey] = useState('');
  const [newAttributeValue, setNewAttributeValue] = useState('');

  useEffect(() => {
    if (selectedEntry) {
      const entryAttributes = selectedEntry.tenantLookupAttributes
        ? _.cloneDeep(selectedEntry.tenantLookupAttributes)
        : {};
      const entryDisplayName = selectedEntry.displayName ? selectedEntry.displayName : '';

      setDisplayName(entryDisplayName);
      setAttributes(entryAttributes);
    }
  }, [selectedEntry]);

  const shouldConfirmButtonBeDisabled = (attributesToCheck, displayNameToCheck) => {
    return (
      _.isEmpty(attributesToCheck) ||
      displayNameToCheck === '' ||
      (_.isEqual(attributesToCheck, selectedEntry.tenantLookupAttributes) &&
        displayNameToCheck === selectedEntry.displayName)
    );
  };

  const updateDisplayName = newName => {
    setDisplayName(newName);
    setConfirmDisabled(shouldConfirmButtonBeDisabled(attributes, newName));
  };

  const addAttribute = () => {
    attributes[newAttributeKey] = newAttributeValue;
    setAttributes(attributes);
    setConfirmDisabled(shouldConfirmButtonBeDisabled(attributes, displayName));
    setNewAttributeKey('');
    setNewAttributeValue('');
  };

  const deleteAttribute = (key, value) => {
    delete attributes[key];
    setAttributes(_.cloneDeep(attributes));
    setConfirmDisabled(shouldConfirmButtonBeDisabled(attributes, displayName));
  };

  const confirmModal = () => {
    onConfirm({
      displayName: displayName,
      tenantLookupAttributes: attributes,
    });
    setConfirmDisabled(true);
    setOpen(false);
    setNewAttributeKey('');
    setNewAttributeValue('');
  };

  const cancelModal = () => {
    setNewAttributeKey('');
    setNewAttributeValue('');
    setConfirmDisabled(true);
    setOpen(false);
  };

  return (
    <Modal
      title="Add/Edit Tenant Configuration"
      open={open}
      scrollContainer="viewport"
      describedById="modal-description"
    >
      <Row className="mb-3">
        <Text>Tenant configuration name</Text>
        <Input
          value={displayName}
          onChange={updateDisplayName}
          type="text"
          size="small"
          placeholder="Enter display name"
          data-testid="lookup-display-name"
        />
      </Row>
      <Text id="modal-description">{'Add or Remove Tenant Lookup Attributes'}</Text>
      <Table headerRows={1} showDividers={true} spacing="small">
        <TableRow key="header">
          <TableCell>
            <Input
              value={newAttributeKey}
              onChange={setNewAttributeKey}
              type="text"
              size="small"
              placeholder="Enter key"
              data-testid="AttributeKey"
            />
          </TableCell>
          <TableCell>
            <Input
              value={newAttributeValue}
              onChange={setNewAttributeValue}
              type="text"
              size="small"
              placeholder="Enter value"
              data-testid="AttributeValue"
            />
          </TableCell>

          <TableCell key="add-button">
            <Button
              data-testid="AddTenantLookupAttribute"
              onClick={addAttribute}
              disabled={!newAttributeKey || !newAttributeValue}
            >
              <Icon tokens={plusTokens} />
            </Button>
          </TableCell>
        </TableRow>

        {Object.entries(attributes).map(([key, value], index) => {
          return (
            <TableRow key={key}>
              <TableCell>{key}</TableCell>
              <TableCell>{value}</TableCell>
              <TableCell>
                <Button
                  type="icon"
                  data-testid={'DeleteTenantLookupAttribute-' + index}
                  onClick={() => deleteAttribute(key, value)}
                >
                  <Icon tokens={trashTokens} />
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
      <ModalFooter>
        <Row alignmentHorizontal="right" widths="fit">
          <Button type="secondary" size="small" onClick={cancelModal}>
            Cancel
          </Button>
          <Button type="primary" size="small" onClick={confirmModal} disabled={confirmDisabled}>
            Confirm
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default TenantLookupModal;
